import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { gatewaysApi } from '../../api/gateways';
import AddressAutocompleteField from '../AddressAutocompleteField';
import GatewayMapView from './GatewayMapView';

const MODE_OPTIONS = [
  { value: '', label: 'Авто' },
  { value: 'AGENT', label: 'Через агента' },
  { value: 'PING', label: 'PING из облака' },
  { value: 'MQTT', label: 'MQTT' },
  { value: 'BOTH', label: 'PING + MQTT' },
];

const TYPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'BS', label: 'Базовая станция' },
  { value: 'USPD', label: 'УСПД' },
];

const INSTALLATION_PLACE_OPTIONS = [
  { value: '', label: 'Не указано' },
  { value: 'indoor', label: 'В помещении' },
  { value: 'facade', label: 'На фасаде' },
  { value: 'roof', label: 'На крыше' },
  { value: 'outdoor', label: 'На улице' },
];

const PLACEMENT_FIELD_LABELS = {
  initial: 'Исходное размещение',
  gateway_type: 'Тип шлюза',
  residential_complex_id: 'Объект',
  building_id: 'Дом',
  corp_id: 'Корпус',
  location: 'Расположение',
  address: 'Адрес',
  latitude: 'Широта',
  longitude: 'Долгота',
  installation_section: 'Подъезд или секция',
  installation_floor: 'Этаж',
  installation_place: 'Место установки',
  antenna_type: 'Тип антенны',
  antenna_height_m: 'Высота антенны',
  antenna_gain_dbi: 'Усиление антенны',
};

export default function GatewayDialog({
  open,
  gateway,
  complexes = [],
  mode = 'view',
  canManage = false,
  onClose,
  onEditRequest,
  onSave,
}) {
  const [form, setForm] = useState({});
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [remoteGateways, setRemoteGateways] = useState([]);
  const [remoteGatewaysLoading, setRemoteGatewaysLoading] = useState(false);
  const [remoteGatewaysError, setRemoteGatewaysError] = useState('');
  const [lorawanHistory, setLorawanHistory] = useState([]);
  const [lorawanHistoryLoading, setLorawanHistoryLoading] = useState(false);
  const [lorawanHistoryError, setLorawanHistoryError] = useState('');
  const [pathHealth, setPathHealth] = useState(null);
  const [pathHealthLoading, setPathHealthLoading] = useState(false);
  const [pathHealthError, setPathHealthError] = useState('');
  const [locationHistory, setLocationHistory] = useState([]);
  const [locationHistoryLoading, setLocationHistoryLoading] = useState(false);
  const [locationHistoryError, setLocationHistoryError] = useState('');

  const navigate = useNavigate();
  const historyPageSize = 50;
  const isEdit = mode === 'edit' || mode === 'create';
  const isBaseStation = form.gateway_type === 'BS';
  const isUspd = form.gateway_type === 'USPD';

  const buildingOptions = useMemo(
    () =>
      (complexes || []).flatMap((complex) =>
        (complex.buildings || []).map((building) => ({
          id: String(building.id),
          residentialComplexId: String(complex.id),
          complexName: complex.name || 'Без названия',
          name: building.name || 'Дом',
          address: building.address || '',
        }))
      ),
    [complexes]
  );
  const selectedBuilding =
    buildingOptions.find((building) => building.id === String(form.building_id || '')) || null;

  useEffect(() => {
    setForm(gateway || {});
    setRemoteGateways([]);
    setRemoteGatewaysError('');
    setLorawanHistory([]);
    setLorawanHistoryError('');
    setPathHealth(null);
    setPathHealthError('');
    setLocationHistory([]);
    setLocationHistoryError('');
    setActiveTab('details');
  }, [gateway]);

  useEffect(() => {
    if (!gateway?.id || !open) {
      setHistory([]);
      setHistoryError('');
      setHistoryOffset(0);
      setHistoryHasMore(false);
      return;
    }
    if (activeTab !== 'history') {
      return;
    }

    const loadHistory = async () => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const data = await gatewaysApi.listHistory(gateway.id, {
          limit: historyPageSize,
          offset: 0,
        });
        const rows = Array.isArray(data) ? data : [];
        setHistory(rows);
        setHistoryOffset(rows.length);
        setHistoryHasMore(rows.length === historyPageSize);
      } catch (error) {
        console.error('load gateway history failed', error);
        setHistoryError('Не удалось загрузить историю статусов');
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [activeTab, gateway?.id, open]);

  useEffect(() => {
    if (!gateway?.id || !open || activeTab !== 'placement') {
      return;
    }
    let cancelled = false;
    const loadLocationHistory = async () => {
      setLocationHistoryLoading(true);
      setLocationHistoryError('');
      try {
        const data = await gatewaysApi.listLocationHistory(gateway.id, { limit: 100 });
        if (!cancelled) setLocationHistory(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('load gateway location history failed', error);
        if (!cancelled) setLocationHistoryError('Не удалось загрузить историю размещения');
      } finally {
        if (!cancelled) setLocationHistoryLoading(false);
      }
    };
    loadLocationHistory();
    return () => {
      cancelled = true;
    };
  }, [activeTab, gateway?.id, open]);

  useEffect(() => {
    if (!gateway?.id || !open || activeTab !== 'devices' || mode === 'create') {
      return;
    }

    let cancelled = false;
    const loadPathHealth = async () => {
      setPathHealthLoading(true);
      setPathHealthError('');
      try {
        const data = await gatewaysApi.getPathHealth(gateway.id);
        if (!cancelled) setPathHealth(data);
      } catch (error) {
        console.error('load gateway path health failed', error);
        if (!cancelled) {
          setPathHealth(null);
          setPathHealthError(error?.response?.data?.detail || 'Не удалось загрузить приборы');
        }
      } finally {
        if (!cancelled) setPathHealthLoading(false);
      }
    };
    loadPathHealth();
    return () => {
      cancelled = true;
    };
  }, [activeTab, gateway?.id, mode, open]);

  useEffect(() => {
    if (!gateway?.id || !open || activeTab !== 'lorawan' || gateway.gateway_type !== 'BS') {
      return;
    }

    const loadLoRaWanHistory = async () => {
      setLorawanHistoryLoading(true);
      setLorawanHistoryError('');
      try {
        const data = await gatewaysApi.listLoRaWanHealthHistory(gateway.id, {
          hours: 168,
          limit: 168,
        });
        setLorawanHistory(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('load gateway lorawan history failed', error);
        setLorawanHistoryError('Не удалось загрузить историю LoRaWAN');
      } finally {
        setLorawanHistoryLoading(false);
      }
    };

    loadLoRaWanHistory();
  }, [activeTab, gateway?.id, gateway?.gateway_type, open]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleLoadRemoteGateways = async () => {
    setRemoteGatewaysLoading(true);
    setRemoteGatewaysError('');
    try {
      const data = await gatewaysApi.listRemoteLoRaWanGateways({ limit: 100 });
      setRemoteGateways(Array.isArray(data) ? data : []);
    } catch (error) {
      setRemoteGateways([]);
      setRemoteGatewaysError(error?.response?.data?.detail || 'Не удалось загрузить базовые станции LoRaWAN');
    } finally {
      setRemoteGatewaysLoading(false);
    }
  };

  const handleUseRemoteGateway = (gatewayId) => {
    const remote = remoteGateways.find((item) => item.gateway_id === gatewayId);
    update('lorawan_gateway_id', gatewayId);
    update('lorawan_gateway_name', remote?.name || '');
    if (!form.name && remote?.name) {
      update('name', remote.name);
    }
  };

  const updateCoordinate = (key, value) => {
    if (value === '') {
      update(key, '');
      return;
    }
    const numeric = Number(value);
    update(key, Number.isFinite(numeric) ? numeric : value);
  };

  const normalizeCoordinate = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const applySuggestionCoordinates = (suggestion) => {
    const lat = suggestion?.data?.geo_lat;
    const lon = suggestion?.data?.geo_lon;
    if (lat == null || lon == null) {
      return;
    }
    const nextLat = Number(lat);
    const nextLon = Number(lon);
    if (Number.isFinite(nextLat) && Number.isFinite(nextLon)) {
      update('latitude', nextLat);
      update('longitude', nextLon);
    }
  };

  const handleSave = () => {
    const payload = {
      ...form,
      latitude: normalizeCoordinate(form.latitude),
      longitude: normalizeCoordinate(form.longitude),
      antenna_height_m: normalizeCoordinate(form.antenna_height_m),
      antenna_gain_dbi: normalizeCoordinate(form.antenna_gain_dbi),
    };
    onSave?.(payload);
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('ru-RU');
    } catch {
      return String(value);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds == null) return '?';
    const total = Math.max(0, Math.floor(Number(seconds)));
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hrs > 0) return `${hrs}ч ${mins}м`;
    if (mins > 0) return `${mins}м ${secs}с`;
    return `${secs}с`;
  };

  const statusColor = (status) => {
    if (status === 'UP') return 'success';
    if (status === 'DOWN') return 'error';
    if (status === 'DEGRADED') return 'warning';
    return 'default';
  };

  const historyRows = useMemo(() => history || [], [history]);
  const historyLabel = historyRows.length ? `История (${historyRows.length})` : 'История';
  const lorawanHistoryRows = useMemo(() => lorawanHistory || [], [lorawanHistory]);
  const latestLoRaWanPoint = lorawanHistoryRows[lorawanHistoryRows.length - 1] || null;
  const maxLoRaWanUplinks = Math.max(1, ...lorawanHistoryRows.map((row) => row.uplinks_count || 0));
  const devicesLabel = pathHealth
    ? `Приборы (${pathHealth.devices_problem}/${pathHealth.devices_total})`
    : gateway?.devices_total != null
      ? `Приборы (${gateway.devices_problem || 0}/${gateway.devices_total || 0})`
      : 'Приборы';

  const pathFocusColor = (focus) => {
    if (focus === 'gateway') return 'error';
    if (focus === 'devices' || focus === 'mixed') return 'warning';
    if (focus === 'ok') return 'success';
    return 'default';
  };

  const deviceStatusColor = (status) => {
    if (status === 'ok') return 'success';
    if (status === 'warn' || status === 'no_data') return 'warning';
    if (status === 'error') return 'error';
    return 'default';
  };

  const mapDraftGateways =
    normalizeCoordinate(form.latitude) !== null && normalizeCoordinate(form.longitude) !== null
      ? [{ ...form, id: form.id || 'draft-gateway' }]
      : [];

  const handleExportCsv = () => {
    if (!historyRows.length) return;
    const header = ['status', 'reason', 'started_at', 'ended_at', 'duration_sec', 'notes'];
    const lines = [header.join(',')];
    historyRows.forEach((row) => {
      const line = [
        row.status ?? '',
        row.reason ?? '',
        row.started_at ?? '',
        row.ended_at ?? '',
        row.duration_sec ?? '',
        (row.notes ?? '').replace(/\r?\n/g, ' ').replace(/"/g, '""'),
      ]
        .map((cell) => `"${String(cell)}"`)
        .join(',');
      lines.push(line);
    });
    const csv = `\ufeff${lines.join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gateway_history_${gateway?.id || 'export'}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleLoadMore = async () => {
    if (!gateway?.id || historyLoadingMore || !historyHasMore) {
      return;
    }
    setHistoryLoadingMore(true);
    setHistoryError('');
    try {
      const data = await gatewaysApi.listHistory(gateway.id, {
        limit: historyPageSize,
        offset: historyOffset,
      });
      const rows = Array.isArray(data) ? data : [];
      setHistory((prev) => [...prev, ...rows]);
      setHistoryOffset((prev) => prev + rows.length);
      setHistoryHasMore(rows.length === historyPageSize);
    } catch (error) {
      console.error('load gateway history failed', error);
      setHistoryError('Не удалось загрузить историю статусов');
    } finally {
      setHistoryLoadingMore(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Новый шлюз' : 'Карточка шлюза'}</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab value="details" label="Детали" />
          {mode !== 'create' && gateway?.id ? <Tab value="devices" label={devicesLabel} /> : null}
          <Tab value="history" label={historyLabel} />
          {mode !== 'create' && gateway?.gateway_type === 'BS' ? <Tab value="placement" label="Размещение" /> : null}
          {gateway?.gateway_type === 'BS' && <Tab value="lorawan" label="LoRaWAN" />}
        </Tabs>

        {activeTab === 'details' && (
          <Stack spacing={2}>
            <TextField
              label="Название"
              value={form.name || ''}
              onChange={(event) => update('name', event.target.value)}
              disabled={!isEdit}
              fullWidth
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Тип"
                value={form.gateway_type || ''}
                onChange={(event) => update('gateway_type', event.target.value)}
                disabled={!isEdit}
                select
                fullWidth
              >
                {TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Режим мониторинга"
                value={form.monitoring_mode || ''}
                onChange={(event) => update('monitoring_mode', event.target.value)}
                disabled={!isEdit}
                select
                fullWidth
                helperText={
                  form.monitoring_mode === 'AGENT'
                    ? 'Доступность проверяет агент на объекте (для локальной сети).'
                    : form.monitoring_mode === 'PING'
                      ? 'Облако само пингует host — нужен доступ из интернета.'
                      : ' '
                }
              >
                {MODE_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'auto'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Heartbeat (сек)"
                value={form.heartbeat_interval_sec || ''}
                onChange={(event) =>
                  update(
                    'heartbeat_interval_sec',
                    event.target.value ? Number(event.target.value) : ''
                  )
                }
                disabled={!isEdit}
                fullWidth
              />
            </Stack>

            {isUspd && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Привязка к дому
                    </Typography>
                    <Chip label="Необязательно" size="small" variant="outlined" />
                  </Stack>
                  <Autocomplete
                    options={buildingOptions}
                    value={selectedBuilding}
                    onChange={(event, building) => {
                      setForm((prev) => ({
                        ...prev,
                        residential_complex_id: building?.residentialComplexId || null,
                        building_id: building?.id || null,
                        corp_id: null,
                      }));
                    }}
                    getOptionLabel={(option) =>
                      [option.name, option.address].filter(Boolean).join(' · ')
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!isEdit}
                    noOptionsText="Дома не найдены"
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <ListItemText
                          primary={option.name}
                          secondary={[option.complexName, option.address].filter(Boolean).join(' · ')}
                        />
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Дом"
                        placeholder="Название или адрес"
                        helperText="Можно оставить без привязки"
                      />
                    )}
                  />
                </Stack>
              </Paper>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Host (ip или домен:порт)"
                value={form.host || ''}
                onChange={(event) => update('host', event.target.value)}
                disabled={!isEdit}
                fullWidth
              />
              <TextField
                label="MQTT topic"
                value={form.mqtt_topic || ''}
                onChange={(event) => update('mqtt_topic', event.target.value)}
                disabled={!isEdit}
                fullWidth
              />
              <TextField
                label="MAC адрес"
                value={form.mac_address || ''}
                onChange={(event) => update('mac_address', event.target.value)}
                disabled={!isEdit}
                fullWidth
              />
            </Stack>

            {isBaseStation && (
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="LoRaWAN Gateway ID"
                    value={form.lorawan_gateway_id || ''}
                    onChange={(event) => update('lorawan_gateway_id', event.target.value)}
                    disabled={!isEdit}
                    fullWidth
                  />
                  <TextField
                    label="Название в LoRaWAN"
                    value={form.lorawan_gateway_name || ''}
                    onChange={(event) => update('lorawan_gateway_name', event.target.value)}
                    disabled={!isEdit}
                    fullWidth
                  />
                </Stack>

                {isEdit && (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={handleLoadRemoteGateways}
                      disabled={remoteGatewaysLoading}
                      sx={{ alignSelf: { xs: 'stretch', md: 'flex-start' } }}
                    >
                      {remoteGatewaysLoading ? 'Загрузка...' : 'Подтянуть БС'}
                    </Button>
                    {remoteGateways.length ? (
                      <TextField
                        label="Выбрать БС"
                        value={form.lorawan_gateway_id || ''}
                        onChange={(event) => handleUseRemoteGateway(event.target.value)}
                        select
                        size="small"
                        sx={{ minWidth: { xs: '100%', md: 360 } }}
                      >
                        {form.lorawan_gateway_id &&
                        !remoteGateways.some((item) => item.gateway_id === form.lorawan_gateway_id) ? (
                          <MenuItem value={form.lorawan_gateway_id}>
                            {form.lorawan_gateway_name
                              ? `${form.lorawan_gateway_name} · ${form.lorawan_gateway_id}`
                              : form.lorawan_gateway_id}
                          </MenuItem>
                        ) : null}
                        {remoteGateways.map((item) => (
                          <MenuItem key={item.gateway_id} value={item.gateway_id}>
                            {[item.name, item.gateway_id].filter(Boolean).join(' · ')}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}
                    {remoteGatewaysError ? (
                      <Typography variant="caption" color="error">
                        {remoteGatewaysError}
                      </Typography>
                    ) : null}
                  </Stack>
                )}
              </Stack>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField label="Статус" value={form.status || '—'} disabled fullWidth />
              <TextField
                label="Последний сигнал"
                value={form.last_seen_at ? new Date(form.last_seen_at).toLocaleString('ru-RU') : '—'}
                disabled
                fullWidth
              />
              <TextField
                label="Латентность (мс)"
                value={form.last_latency_ms ?? '—'}
                disabled
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Ответственный"
                value={form.responsible_name || ''}
                onChange={(event) => update('responsible_name', event.target.value)}
                disabled={!isEdit}
                fullWidth
              />
              <TextField
                label="Телефон"
                value={form.responsible_phone || ''}
                onChange={(event) => update('responsible_phone', event.target.value)}
                disabled={!isEdit}
                fullWidth
              />
              <TextField
                label="Email"
                value={form.responsible_email || ''}
                onChange={(event) => update('responsible_email', event.target.value)}
                disabled={!isEdit}
                fullWidth
              />
            </Stack>

            <AddressAutocompleteField
              label="Адрес"
              value={form.address || ''}
              onValueChange={(value) => update('address', value)}
              onSuggestionSelect={(suggestion) => {
                if (!suggestion) {
                  return;
                }
                update('address', suggestion.value || '');
                applySuggestionCoordinates(suggestion);
              }}
              disabled={!isEdit}
            />
            <TextField
              label="Расположение"
              value={form.location || ''}
              onChange={(event) => update('location', event.target.value)}
              disabled={!isEdit}
              fullWidth
            />

            {isBaseStation && (
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Установка БС
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Подъезд или секция"
                    value={form.installation_section || ''}
                    onChange={(event) => update('installation_section', event.target.value)}
                    disabled={!isEdit}
                    fullWidth
                  />
                  <TextField
                    label="Этаж установки"
                    value={form.installation_floor || ''}
                    onChange={(event) => update('installation_floor', event.target.value)}
                    disabled={!isEdit}
                    fullWidth
                  />
                  <TextField
                    label="Место установки"
                    value={form.installation_place || ''}
                    onChange={(event) => update('installation_place', event.target.value)}
                    disabled={!isEdit}
                    select
                    fullWidth
                  >
                    {INSTALLATION_PLACE_OPTIONS.map((option) => (
                      <MenuItem key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Тип антенны"
                    value={form.antenna_type || ''}
                    onChange={(event) => update('antenna_type', event.target.value)}
                    disabled={!isEdit}
                    fullWidth
                  />
                  <TextField
                    label="Высота антенны, м"
                    value={form.antenna_height_m ?? ''}
                    onChange={(event) => update('antenna_height_m', event.target.value)}
                    disabled={!isEdit}
                    inputProps={{ inputMode: 'decimal', step: '0.1', min: 0 }}
                    fullWidth
                  />
                  <TextField
                    label="Усиление антенны, dBi"
                    value={form.antenna_gain_dbi ?? ''}
                    onChange={(event) => update('antenna_gain_dbi', event.target.value)}
                    disabled={!isEdit}
                    inputProps={{ inputMode: 'decimal', step: '0.1' }}
                    fullWidth
                  />
                </Stack>
                {mode !== 'create' && isEdit ? (
                  <TextField
                    label="Причина изменения размещения"
                    value={form.location_change_reason || ''}
                    onChange={(event) => update('location_change_reason', event.target.value)}
                    helperText="Заполняется при переносе БС или изменении антенны"
                    fullWidth
                  />
                ) : null}
              </Stack>
            )}

            <Stack spacing={1.25}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Координаты и точка на карте
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Широта"
                  value={form.latitude ?? ''}
                  onChange={(event) => updateCoordinate('latitude', event.target.value)}
                  disabled={!isEdit}
                  fullWidth
                  inputProps={{ inputMode: 'decimal', step: 'any' }}
                  helperText="Например: 55.751244"
                />
                <TextField
                  label="Долгота"
                  value={form.longitude ?? ''}
                  onChange={(event) => updateCoordinate('longitude', event.target.value)}
                  disabled={!isEdit}
                  fullWidth
                  inputProps={{ inputMode: 'decimal', step: 'any' }}
                  helperText="Например: 37.618423"
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Можно ввести координаты вручную или выбрать точку кликом по карте.
                </Typography>
                {isEdit && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      update('latitude', null);
                      update('longitude', null);
                    }}
                  >
                    Очистить точку
                  </Button>
                )}
              </Stack>
              <GatewayMapView
                gateways={mapDraftGateways}
                selectedGatewayId={form.id || 'draft-gateway'}
                interactive={isEdit}
                height={260}
                onPickCoordinates={({ latitude, longitude }) => {
                  if (!isEdit) return;
                  update('latitude', latitude);
                  update('longitude', longitude);
                }}
              />
            </Stack>

            <TextField
              label="Доступы"
              value={form.credentials || ''}
              onChange={(event) => update('credentials', event.target.value)}
              disabled={!isEdit}
              fullWidth
            />
            <TextField
              label="Заметки"
              value={form.notes || ''}
              onChange={(event) => update('notes', event.target.value)}
              disabled={!isEdit}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        )}

        {activeTab === 'devices' && mode !== 'create' && (
          <Stack spacing={2}>
            {pathHealthLoading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Загрузка приборов...</Typography>
              </Stack>
            )}

            {pathHealthError && <Alert severity="error">{pathHealthError}</Alert>}

            {!pathHealthLoading && pathHealth && (
              <>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip
                    color={pathFocusColor(pathHealth.path_issue_focus)}
                    label={pathHealth.path_issue_label}
                  />
                  <Chip
                    variant="outlined"
                    label={`Проблемных: ${pathHealth.devices_problem} / ${pathHealth.devices_total}`}
                  />
                  <Chip variant="outlined" label={`В норме: ${pathHealth.devices_ok}`} />
                </Stack>
                {pathHealth.path_issue_reason ? (
                  <Typography variant="body2" color="text.secondary">
                    {pathHealth.path_issue_reason}
                  </Typography>
                ) : null}

                {!pathHealth.devices.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Привязанных приборов нет
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {pathHealth.devices.map((device) => (
                      <ListItem
                        key={device.id}
                        disablePadding
                        secondaryAction={
                          <Chip
                            size="small"
                            color={deviceStatusColor(device.status)}
                            label={device.status || '—'}
                          />
                        }
                      >
                        <ListItemButton
                          disabled={!device.apartment_id}
                          onClick={() => {
                            if (!device.apartment_id) return;
                            onClose?.();
                            navigate(`/apartments/${device.apartment_id}`);
                          }}
                          sx={{ borderRadius: 2, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={
                              [
                                device.apartment_number ? `кв. ${device.apartment_number}` : null,
                                device.serial_number || 'без серийника',
                                device.system_type,
                              ]
                                .filter(Boolean)
                                .join(' · ')
                            }
                            secondary={
                              device.is_problem
                                ? `Проблема · последние данные: ${device.last_data_time || 'нет'}`
                                : `OK · последние данные: ${device.last_data_time || '—'}`
                            }
                            primaryTypographyProps={{ fontWeight: device.is_problem ? 700 : 500 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Stack>
        )}

        {activeTab === 'history' && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="subtitle1">История статусов</Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="outlined"
                onClick={handleExportCsv}
                disabled={!historyRows.length}
              >
                Экспорт CSV
              </Button>
            </Stack>

            {historyLoading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Загрузка истории...</Typography>
              </Stack>
            )}

            {historyError && (
              <Typography variant="body2" color="error">
                {historyError}
              </Typography>
            )}

            {!historyLoading && !historyError && historyRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                История пока пустая
              </Typography>
            )}

            {historyRows.length > 0 && (
              <List dense sx={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 1 }}>
                {historyRows.map((row, index) => {
                  const range = `${formatDate(row.started_at)} - ${formatDate(row.ended_at)}`;
                  return (
                    <React.Fragment key={row.id || index}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" color={statusColor(row.status)} label={row.status || '?'} />
                              <Typography variant="body2" color="text.secondary">
                                {range}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap">
                              <Typography variant="caption" color="text.secondary">
                                Длительность: {formatDuration(row.duration_sec)}
                              </Typography>
                              {row.reason && (
                                <Typography variant="caption" color="text.secondary">
                                  Причина: {row.reason}
                                </Typography>
                              )}
                              {row.notes && (
                                <Typography variant="caption" color="text.secondary">
                                  {row.notes}
                                </Typography>
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index < historyRows.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}

            {historyRows.length > 0 && historyHasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={historyLoadingMore}
                >
                  {historyLoadingMore ? 'Загрузка…' : 'Показать ещё'}
                </Button>
              </Box>
            )}
          </Stack>
        )}

        {activeTab === 'placement' && gateway?.gateway_type === 'BS' && (
          <Stack spacing={1.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>История размещения</Typography>
            {locationHistoryLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Загрузка...</Typography>
              </Stack>
            ) : null}
            {locationHistoryError ? <Typography variant="body2" color="error">{locationHistoryError}</Typography> : null}
            {!locationHistoryLoading && !locationHistoryError && !locationHistory.length ? (
              <Typography variant="body2" color="text.secondary">История пока пустая</Typography>
            ) : null}
            {locationHistory.length ? (
              <List disablePadding sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                {locationHistory.map((row, index) => {
                  const placeLabel = INSTALLATION_PLACE_OPTIONS.find((item) => item.value === row.installation_place)?.label;
                  const position = [
                    row.address || row.location,
                    row.installation_section ? `секция ${row.installation_section}` : null,
                    row.installation_floor ? `${row.installation_floor} этаж` : null,
                    placeLabel && row.installation_place ? placeLabel : null,
                  ].filter(Boolean).join(' · ');
                  const antenna = [
                    row.antenna_type,
                    row.antenna_height_m != null ? `${row.antenna_height_m} м` : null,
                    row.antenna_gain_dbi != null ? `${row.antenna_gain_dbi} dBi` : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <React.Fragment key={row.id}>
                      <ListItem alignItems="flex-start" sx={{ py: 1.1 }}>
                        <ListItemText
                          primary={(
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{position || 'Место не указано'}</Typography>
                              {!row.valid_to ? <Chip size="small" color="success" variant="outlined" label="Текущее" /> : null}
                            </Stack>
                          )}
                          secondary={(
                            <Stack spacing={0.35} sx={{ mt: 0.45 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(row.valid_from)} - {row.valid_to ? formatDate(row.valid_to) : 'сейчас'}
                              </Typography>
                              {row.latitude != null && row.longitude != null ? (
                                <Typography variant="caption" color="text.secondary">{row.latitude}, {row.longitude}</Typography>
                              ) : null}
                              {antenna ? <Typography variant="caption" color="text.secondary">Антенна: {antenna}</Typography> : null}
                              {row.changed_fields?.length ? (
                                <Typography variant="caption" color="text.secondary">
                                  Изменено: {row.changed_fields.map((field) => PLACEMENT_FIELD_LABELS[field] || field).join(', ')}
                                </Typography>
                              ) : null}
                              {row.change_reason ? <Typography variant="caption">Причина: {row.change_reason}</Typography> : null}
                              {row.changed_by_name ? <Typography variant="caption" color="text.secondary">Сотрудник: {row.changed_by_name}</Typography> : null}
                            </Stack>
                          )}
                        />
                      </ListItem>
                      {index < locationHistory.length - 1 ? <Divider /> : null}
                    </React.Fragment>
                  );
                })}
              </List>
            ) : null}
          </Stack>
        )}

        {activeTab === 'lorawan' && gateway?.gateway_type === 'BS' && (
          <Stack spacing={2}>
            {lorawanHistoryLoading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Загрузка истории...</Typography>
              </Stack>
            )}

            {lorawanHistoryError && (
              <Typography variant="body2" color="error">
                {lorawanHistoryError}
              </Typography>
            )}

            {!lorawanHistoryLoading && !lorawanHistoryError && lorawanHistoryRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                История LoRaWAN пока пустая
              </Typography>
            )}

            {latestLoRaWanPoint && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                {[
                  { label: 'Uplink', value: latestLoRaWanPoint.uplinks_count },
                  { label: 'Приборы', value: latestLoRaWanPoint.unique_devices_count },
                  { label: 'ACK', value: latestLoRaWanPoint.ack_rate == null ? '—' : `${latestLoRaWanPoint.ack_rate}%` },
                  {
                    label: 'RSSI / SNR',
                    value: `${latestLoRaWanPoint.rssi_avg == null ? '—' : Number(latestLoRaWanPoint.rssi_avg).toFixed(1)} / ${
                      latestLoRaWanPoint.snr_avg == null ? '—' : Number(latestLoRaWanPoint.snr_avg).toFixed(1)
                    }`,
                  },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      p: 1.25,
                      borderRadius: 2.5,
                      borderColor: 'rgba(15,23,42,0.08)',
                      background: 'rgba(248,250,252,0.78)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}

            {lorawanHistoryRows.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2.5,
                  borderColor: 'rgba(15,23,42,0.08)',
                  background: '#fff',
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Динамика за 7 дней
                  </Typography>
                  <Box
                    sx={{
                      height: 86,
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 0.5,
                      px: 0.5,
                      borderRadius: 2,
                      background: 'linear-gradient(180deg, rgba(248,250,252,0.72), rgba(241,245,249,0.72))',
                    }}
                  >
                    {lorawanHistoryRows.slice(-72).map((row, index) => {
                      const height = Math.max(5, Math.round(((row.uplinks_count || 0) / maxLoRaWanUplinks) * 72));
                      const isWarning = row.health_status && row.health_status !== 'good';
                      return (
                        <Tooltip
                          key={`${row.period_end}-${index}`}
                          title={`${formatDate(row.period_end)} · uplink: ${row.uplinks_count || 0}`}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 3,
                              height,
                              borderRadius: '6px 6px 2px 2px',
                              background: isWarning ? '#F59E0B' : '#2563EB',
                              opacity: isWarning ? 0.95 : 0.82,
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                  {latestLoRaWanPoint.reasons?.length ? (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      {latestLoRaWanPoint.reasons.map((reason) => (
                        <Chip key={reason} size="small" color="warning" variant="outlined" label={reason} />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
        {!isEdit && canManage && (
          <Button variant="outlined" onClick={onEditRequest}>
            Редактировать
          </Button>
        )}
        {isEdit && (
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
